<template>
  <div class="display-editor text-[11px] leading-4">
    <div class="sticky top-0 z-10 mb-3 flex items-start justify-between gap-2 border-b border-gray-200 bg-white pb-3 pt-1">
      <div>
        <h3 class="text-xs font-semibold text-gray-800">Display configuration</h3>
        <p class="mt-1 text-gray-500">Applies to every entity of this type.</p>
        <span v-if="dirty" class="mt-1 block text-primary-700">Unsaved changes</span>
        <span v-else-if="saved" role="status" class="mt-1 block text-gray-500">Saved</span>
      </div>
      <div class="flex gap-2">
        <button type="button" class="display-button" :disabled="!dirty || saving" @click="discard">Discard</button>
        <button type="button" class="display-button display-primary" :disabled="!dirty || saving" @click="save">{{ saving ? 'Saving…' : 'Save' }}</button>
      </div>
    </div>
    <p v-if="error" role="alert" class="mb-3 text-red-700">{{ error }}</p>
    <fieldset :disabled="saving" class="min-w-0 space-y-3">
      <details open class="overflow-hidden rounded-lg border border-gray-200">
        <summary class="cursor-pointer bg-gray-50 px-3 py-3 text-xs font-semibold text-gray-800">
          Entity page layout <span class="text-[11px] font-normal text-gray-500">({{ orderedTabs.length }} tabs)</span>
          <span class="mt-1 block text-[11px] font-normal text-gray-500">Tabs and components shown when you open an individual entity.</span>
        </summary>
        <div class="space-y-3 border-t border-gray-200 bg-white p-3">
          <section v-for="(tab, tabIndex) in orderedTabs" :key="tab" class="overflow-hidden rounded-lg border border-gray-200">
            <div class="flex items-center gap-2 bg-white px-2 py-2">
              <button type="button" class="display-icon" :aria-expanded="expandedTab === tab" :aria-label="`Expand ${tab}`" @click="expandedTab = expandedTab === tab ? null : tab">
                <FontAwesomeIcon :icon="['fas', 'chevron-right']" :class="{ 'rotate-90': expandedTab === tab }" />
              </button>
              <input :value="tab" :aria-label="`Rename ${tab} tab`" title="Click to rename tab" class="min-w-0 flex-1 rounded border border-transparent bg-transparent px-1 py-1 text-xs font-medium text-gray-800 hover:border-gray-300 focus:border-neutral-500 focus:outline-none" @change="renameTab(tab, $event)" @keydown.enter.prevent="$event.target.blur()" @keydown.esc.prevent="$event.target.value = tab; $event.target.blur()" />
              <span class="tabular-nums text-gray-500">{{ draft.layout_config.tabs[tab].length }}</span>
              <button type="button" class="display-icon" :disabled="tabIndex === 0" title="Move tab up" aria-label="Move tab up" @click="moveTab(tabIndex, -1)"><FontAwesomeIcon :icon="['fas', 'arrow-up']" /></button>
              <button type="button" class="display-icon" :disabled="tabIndex === orderedTabs.length - 1" title="Move tab down" aria-label="Move tab down" @click="moveTab(tabIndex, 1)"><FontAwesomeIcon :icon="['fas', 'arrow-down']" /></button>
              <button type="button" class="display-icon display-remove" title="Remove tab" :aria-label="`Remove ${tab} tab`" @click="removeTab(tab)"><FontAwesomeIcon :icon="['fas', 'xmark']" /></button>
            </div>
            <div v-if="expandedTab === tab" class="border-t border-gray-200 p-2">
              <div v-for="(component, index) in draft.layout_config.tabs[tab]" :key="index" class="border-b border-gray-100 last:border-0">
                <div class="flex items-center gap-2 py-2">
                  <span class="w-4 shrink-0 text-center tabular-nums text-gray-400">{{ index + 1 }}</span>
                  <span class="min-w-0 flex-1 break-words font-medium text-gray-700">{{ componentLabel(component.component) }}</span>
                  <button type="button" class="display-icon" :aria-expanded="settingsComponent === component" title="Component settings" aria-label="Component settings" @click="settingsComponent = settingsComponent === component ? null : component"><FontAwesomeIcon :icon="['fas', 'gear']" /></button>
                  <button type="button" class="display-icon" :disabled="index === 0" title="Move component up" aria-label="Move component up" @click="moveComponent(tab, index, -1)"><FontAwesomeIcon :icon="['fas', 'arrow-up']" /></button>
                  <button type="button" class="display-icon" :disabled="index === draft.layout_config.tabs[tab].length - 1" title="Move component down" aria-label="Move component down" @click="moveComponent(tab, index, 1)"><FontAwesomeIcon :icon="['fas', 'arrow-down']" /></button>
                  <button type="button" class="display-icon display-remove" title="Remove component" aria-label="Remove component" @click="removeComponent(tab, index)"><FontAwesomeIcon :icon="['fas', 'xmark']" /></button>
                </div>
                <div v-if="settingsComponent === component" class="mb-2 space-y-2 rounded border border-gray-200 bg-gray-50 p-2">
                  <label class="display-label">Component
                    <select :value="component.component" class="display-input" @change="changeComponent(component, $event.target.value)">
                      <option v-if="!componentOptions.some(option => option.value === component.component)" :value="component.component">{{ componentLabel(component.component) }}</option>
                      <option v-for="option in componentOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
                    </select>
                  </label>
                  <template v-if="component.component === 'entity_detail_table'">
                    <label class="display-label">Custom title<input :value="component.settings?.title || ''" class="display-input" @input="setSetting(component, 'title', $event.target.value)" /></label>
                    <label class="display-label">Child entity type
                      <select :value="component.settings?.child_entity_type_id ?? ''" class="display-input" @change="setChildType(component, $event.target.value)">
                        <option value="">Select entity type…</option>
                        <option v-for="type in entityTypes" :key="type.id" :value="type.id">{{ type.type_name }}</option>
                      </select>
                    </label>
                    <label class="display-label">Entity name for create button<input :value="component.settings?.entity_type_name || ''" class="display-input" @input="setSetting(component, 'entity_type_name', $event.target.value)" /></label>
                    <label class="flex items-center gap-2 text-gray-600"><input type="checkbox" :checked="!!component.settings?.auto_height" @change="setSetting(component, 'auto_height', $event.target.checked)" /> Auto height</label>
                    <p class="text-gray-500">Configure its columns in the separate Detail-table layout section below.</p>
                  </template>
                </div>
              </div>
              <p v-if="!draft.layout_config.tabs[tab].length" class="py-3 text-gray-500">No components in this tab.</p>
              <div v-if="pickerTab === tab" class="mt-2 rounded border border-gray-200 bg-gray-50 p-2">
                <div class="mb-2 flex items-center gap-2">
                  <input v-model="componentSearch" type="search" class="display-input min-w-0 flex-1" placeholder="Search components…" aria-label="Search components" />
                  <button type="button" class="display-icon" aria-label="Close component picker" @click="pickerTab = null"><FontAwesomeIcon :icon="['fas', 'xmark']" /></button>
                </div>
                <ul class="max-h-48 overflow-y-auto rounded border border-gray-200 bg-white">
                  <li v-for="option in filteredComponents" :key="option.value"><button type="button" class="flex w-full items-center justify-between gap-2 border-b border-gray-100 px-2 py-2 text-left hover:bg-gray-50" @click="addComponent(tab, option.value)"><span>{{ option.label }}</span><FontAwesomeIcon :icon="['fas', 'plus']" class="text-gray-400" /></button></li>
                  <li v-if="!filteredComponents.length" class="px-2 py-3 text-gray-500">No matching components.</li>
                </ul>
              </div>
              <button v-else type="button" class="display-button mt-2" @click="pickerTab = tab; componentSearch = ''"><FontAwesomeIcon :icon="['fas', 'plus']" /> Add component</button>
            </div>
          </section>
          <p v-if="!orderedTabs.length" class="rounded border border-dashed border-gray-300 p-4 text-gray-500">No page tabs configured. Add a tab to begin.</p>
          <form class="flex items-center gap-2" @submit.prevent="addTab">
            <input v-model="newTabName" class="display-input min-w-0 flex-1" placeholder="New tab name…" aria-label="New tab name" />
            <button type="submit" class="display-button shrink-0" :disabled="!newTabName.trim()"><FontAwesomeIcon :icon="['fas', 'plus']" /> Add tab</button>
          </form>
        </div>
      </details>
      <details class="overflow-hidden rounded-lg border border-gray-200">
        <summary class="cursor-pointer bg-gray-50 px-3 py-3 text-xs font-semibold text-gray-800">
          Detail-table layout <span class="text-[11px] font-normal text-gray-500">({{ columns.length }} columns)</span>
          <span class="mt-1 block text-[11px] font-normal text-gray-500">Columns, widths and sorting inside Entity Detail Table widgets.</span>
        </summary>
        <div class="space-y-2 border-t border-gray-200 bg-white p-3">
          <p class="mb-3 text-gray-500">These settings are separate from page tabs and do not change the entity list in this admin panel.</p>
          <div v-for="(column, index) in columns" :key="index" class="rounded border border-gray-200">
            <div class="flex items-center gap-2 p-2">
              <span class="min-w-0 flex-1 break-words font-medium text-gray-700">{{ column.header_name || columnSource(column) }}</span>
              <button type="button" class="display-icon" title="Column settings" aria-label="Column settings" :aria-expanded="settingsColumn === column" @click="settingsColumn = settingsColumn === column ? null : column"><FontAwesomeIcon :icon="['fas', 'gear']" /></button>
              <button type="button" class="display-icon" :disabled="index === 0" title="Move column up" aria-label="Move column up" @click="moveItem(columns, index, -1)"><FontAwesomeIcon :icon="['fas', 'arrow-up']" /></button>
              <button type="button" class="display-icon" :disabled="index === columns.length - 1" title="Move column down" aria-label="Move column down" @click="moveItem(columns, index, 1)"><FontAwesomeIcon :icon="['fas', 'arrow-down']" /></button>
              <button type="button" class="display-icon display-remove" title="Remove column" aria-label="Remove column" @click="columns.splice(index, 1)"><FontAwesomeIcon :icon="['fas', 'xmark']" /></button>
            </div>
            <div v-if="settingsColumn === column" class="space-y-2 border-t border-gray-200 bg-gray-50 p-2">
              <p class="text-gray-500">{{ columnSource(column) }}</p>
              <label class="display-label">Column heading<input v-model="column.header_name" class="display-input" /></label>
              <label class="display-label">Width (px)<input v-model.number="column.width" type="number" min="1" class="display-input" /></label>
              <label v-if="column.type === 'identifier'" class="display-label">Identifier type
                <select v-model="column.identifier_type" class="display-input"><option v-for="type in identifierTypes" :key="type" :value="type">{{ type }}</option></select>
              </label>
              <label v-if="column.type === 'action'" class="display-label">Action
                <select v-model="column.action_type" class="display-input"><option value="sync">Sync</option><option value="delete">Delete</option></select>
              </label>
              <div class="flex flex-wrap gap-4 text-gray-600">
                <label class="flex items-center gap-2"><input v-model="column.sortable" type="checkbox" /> Sortable</label>
                <label class="flex items-center gap-2"><input v-model="column.filterable" type="checkbox" /> Filterable</label>
              </div>
            </div>
          </div>
          <p v-if="!columns.length" class="py-2 text-gray-500">No columns configured.</p>
          <form class="flex items-center gap-2 pt-2" @submit.prevent="addColumn">
            <select v-model="newColumn" class="display-input min-w-0 flex-1" aria-label="Add detail-table column">
              <option value="">Choose a column…</option>
              <optgroup label="Attributes"><option v-for="attribute in availableAttributes" :key="attribute.id" :value="`attribute:${attribute.name}`">{{ attribute.display_name }}</option></optgroup>
              <optgroup label="Other"><option value="identifier">Identifier</option><option value="action">Action</option></optgroup>
            </select>
            <button type="submit" class="display-button" :disabled="!newColumn">Add</button>
          </form>
        </div>
      </details>
    </fieldset>
  </div>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faChevronRight, faGear, faArrowUp, faArrowDown, faXmark, faPlus } from '@fortawesome/free-solid-svg-icons';

library.add(faChevronRight, faGear, faArrowUp, faArrowDown, faXmark, faPlus);

const COMPONENT_OPTIONS = [
  {
    "value": "basic_info",
    "label": "Basic Information"
  },
  {
    "value": "entity_context",
    "label": "Entity Context"
  },
  {
    "value": "action_table",
    "label": "Action Items"
  },
  {
    "value": "data_insights",
    "label": "Data Insights"
  },
  {
    "value": "engagement_table",
    "label": "Engagements"
  },
  {
    "value": "entity_detail_table",
    "label": "Entity Detail Table (Dynamic)"
  },
  {
    "value": "staff_table",
    "label": "Staff (Legacy)"
  },
  {
    "value": "threads",
    "label": "Threads"
  },
  {
    "value": "surveys",
    "label": "Surveys"
  },
  {
    "value": "key_metrics_detailed",
    "label": "Key Metrics - Detailed"
  },
  {
    "value": "composable_key_metrics",
    "label": "Key Metrics (Composable)"
  },
  {
    "value": "attribution",
    "label": "Contract Attribution"
  },
  {
    "value": "attribution_health",
    "label": "Attribution Health"
  },
  {
    "value": "strategic_assessments",
    "label": "Strategic Assessments"
  },
  {
    "value": "adt_alerts",
    "label": "ADT Alerts (Admin Only)"
  },
  {
    "value": "toc_sankey",
    "label": "Transitions of Care (Sankey)"
  },
  {
    "value": "entity_table",
    "label": "Entity List"
  },
  {
    "value": "quality_scorecard",
    "label": "Quality Patient Worklist"
  },
  {
    "value": "quality_provider_scorecard",
    "label": "Quality Scorecard"
  },
  {
    "value": "care_gap_summary",
    "label": "Care Gap Summary"
  },
  {
    "value": "ppi_module",
    "label": "PPI"
  },
  {
    "value": "patient_worklist",
    "label": "Patient Worklist"
  },
  {
    "value": "intelligence",
    "label": "Pre-Meeting Brief"
  },
  {
    "value": "engagement_tracker",
    "label": "Engagement Tracker"
  },
  {
    "value": "entity_reports",
    "label": "Intelligence Reports"
  },
  {
    "value": "actuarial_benchmarks",
    "label": "Actuarial Benchmarks"
  },
  {
    "value": "engagement_intelligence_notice",
    "label": "Engagement Intelligence Notice (Banner)"
  },
  {
    "value": "requirement_table",
    "label": "Notes"
  }
];

function normalizeDisplay(config) {
  const draft = JSON.parse(JSON.stringify(config || {}));
  draft.layout_config ||= {};
  draft.layout_config.tabs ||= {};
  const names = Object.keys(draft.layout_config.tabs);
  draft.layout_config.tab_order = [...new Set([...(draft.layout_config.tab_order || []).filter(name => names.includes(name)), ...names])];
  for (const components of Object.values(draft.layout_config.tabs)) components.sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
  draft.table_config ||= {};
  draft.table_config.columns ||= [];
  return draft;
}

export default {
  name: 'EntityDisplayEditor',
  components: { FontAwesomeIcon },
  props: {
    entityType: { type: Object, required: true },
    entityTypes: { type: Array, default: () => [] },
    attributes: { type: Array, default: () => [] },
  },
  emits: ['dirty-change', 'saving-change', 'saved'],
  data() {
    const draft = normalizeDisplay(this.entityType.display_config);
    return { draft, baseline: JSON.stringify(draft), saving: false, saved: false, error: '', expandedTab: draft.layout_config.tab_order[0] || null, newTabName: '', pickerTab: null, componentSearch: '', settingsComponent: null, settingsColumn: null, newColumn: '' };
  },
  computed: {
    componentOptions() { return COMPONENT_OPTIONS; },
    orderedTabs() { return this.draft.layout_config.tab_order; },
    columns() { return this.draft.table_config.columns; },
    dirty() { return JSON.stringify(this.draft) !== this.baseline; },
    filteredComponents() {
      const query = this.componentSearch.trim().toLowerCase();
      return this.componentOptions.filter(option => `${option.label} ${option.value}`.toLowerCase().includes(query));
    },
    availableAttributes() {
      const used = new Set(this.columns.filter(column => column.type === 'attribute').map(column => column.attribute_name));
      return this.attributes.filter(attribute => !used.has(attribute.name));
    },
    identifierTypes() {
      return [...new Set(['NPI', 'TIN', 'MRN', 'BENE_MBI_ID', ...this.columns.filter(column => column.type === 'identifier').map(column => column.identifier_type).filter(Boolean)])];
    },
  },
  watch: {
    dirty(value) { if (value) this.saved = false; this.$emit('dirty-change', value); },
    saving(value) { this.$emit('saving-change', value); },
  },
  methods: {
    componentLabel(value) {
      return this.componentOptions.find(option => option.value === value)?.label || (value || 'Unconfigured component').replaceAll('_', ' ');
    },
    moveItem(items, index, direction) {
      const target = index + direction;
      if (target < 0 || target >= items.length) return;
      [items[index], items[target]] = [items[target], items[index]];
    },
    addTab() {
      const name = this.newTabName.trim();
      if (!name) return;
      if (Object.hasOwn(this.draft.layout_config.tabs, name) || ['__proto__', 'constructor', 'prototype'].includes(name)) { this.error = 'Choose a different tab name.'; return; }
      this.draft.layout_config.tabs[name] = [];
      this.orderedTabs.push(name);
      this.expandedTab = name;
      this.newTabName = '';
      this.error = '';
    },
    renameTab(oldName, event) {
      const name = event.target.value.trim();
      if (name === oldName) return;
      if (!name || Object.hasOwn(this.draft.layout_config.tabs, name) || ['__proto__', 'constructor', 'prototype'].includes(name)) { event.target.value = oldName; this.error = 'Tab names must be non-empty and unique.'; return; }
      this.draft.layout_config.tabs[name] = this.draft.layout_config.tabs[oldName];
      delete this.draft.layout_config.tabs[oldName];
      this.draft.layout_config.tab_order = this.orderedTabs.map(tab => tab === oldName ? name : tab);
      if (this.expandedTab === oldName) this.expandedTab = name;
      if (this.pickerTab === oldName) this.pickerTab = name;
      this.error = '';
    },
    moveTab(index, direction) { this.moveItem(this.orderedTabs, index, direction); },
    removeTab(tab) {
      delete this.draft.layout_config.tabs[tab];
      this.draft.layout_config.tab_order = this.orderedTabs.filter(name => name !== tab);
      if (this.expandedTab === tab) this.expandedTab = this.orderedTabs[0] || null;
      if (this.pickerTab === tab) this.pickerTab = null;
      this.settingsComponent = null;
    },
    addComponent(tab, value) {
      const components = this.draft.layout_config.tabs[tab];
      const component = { component: value, order: components.length, settings: {} };
      this.changeComponent(component, value);
      components.push(component);
      components.forEach((entry, order) => { entry.order = order; });
      this.pickerTab = null;
    },
    changeComponent(component, value) {
      component.component = value;
      if (value === 'entity_detail_table') component.settings = { title: '', child_entity_type_id: null, entity_type_name: '', auto_height: false, ...component.settings };
    },
    moveComponent(tab, index, direction) {
      const components = this.draft.layout_config.tabs[tab];
      this.moveItem(components, index, direction);
      components.forEach((component, order) => { component.order = order; });
    },
    removeComponent(tab, index) {
      const components = this.draft.layout_config.tabs[tab];
      if (components[index] === this.settingsComponent) this.settingsComponent = null;
      components.splice(index, 1);
      components.forEach((component, order) => { component.order = order; });
    },
    setSetting(component, key, value) {
      component.settings ||= {};
      component.settings[key] = value;
    },
    setChildType(component, value) {
      const type = this.entityTypes.find(type => type.id === Number(value));
      this.setSetting(component, 'child_entity_type_id', value ? Number(value) : null);
      this.setSetting(component, 'entity_type_name', type?.type_name || '');
    },
    columnSource(column) {
      if (column.type === 'attribute') return this.attributes.find(attribute => attribute.name === column.attribute_name)?.display_name || column.attribute_name;
      if (column.type === 'identifier') return column.identifier_type || 'Identifier';
      return column.action_type || 'Action';
    },
    addColumn() {
      if (!this.newColumn) return;
      if (this.newColumn.startsWith('attribute:')) {
        const name = this.newColumn.slice('attribute:'.length);
        const attribute = this.attributes.find(attribute => attribute.name === name);
        if (!attribute) return;
        this.columns.push({ type: 'attribute', attribute_name: name, header_name: attribute.display_name, width: 150, sortable: true, filterable: true });
      } else if (this.newColumn === 'identifier') {
        this.columns.push({ type: 'identifier', identifier_type: 'NPI', header_name: 'NPI', width: 120, sortable: true, filterable: true });
      } else this.columns.push({ type: 'action', action_type: 'sync', header_name: 'Sync', width: 80, sortable: false, filterable: false });
      this.newColumn = '';
      this.settingsColumn = this.columns[this.columns.length - 1];
    },
    discard() {
      this.draft = JSON.parse(this.baseline);
      this.error = '';
      this.saved = false;
      this.settingsComponent = null;
      this.settingsColumn = null;
      this.pickerTab = null;
      this.newTabName = '';
      this.expandedTab = this.orderedTabs[0] || null;
    },
    async save() {
      if (!this.dirty || this.saving) return;
      this.saving = true;
      this.error = '';
      const id = this.entityType.id;
      const config = JSON.parse(JSON.stringify(this.draft));
      try {
        const { data } = await axios.patch(`/api/entity-types/${id}/`, { display_config: config });
        this.draft = normalizeDisplay(data.display_config || config);
        this.baseline = JSON.stringify(this.draft);
        this.settingsComponent = null;
        this.settingsColumn = null;
        this.saved = true;
        this.$emit('dirty-change', false);
        this.$emit('saved', { id, display_config: this.draft });
      } catch (error) {
        this.error = error.response?.data?.detail || 'Could not save display configuration.';
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
@reference "../../assets/theme.css";
.display-button {
  @apply inline-flex h-6 items-center justify-center gap-1 rounded border border-gray-300 bg-white px-2 text-[11px] font-medium text-gray-700 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-neutral-500 disabled:cursor-not-allowed disabled:opacity-40;
}
.display-primary {
  @apply border-primary-500 bg-primary-500 text-white hover:bg-primary-600;
}
.display-icon {
  @apply inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-[11px] text-gray-500 hover:bg-gray-100 hover:text-gray-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-neutral-500 disabled:cursor-not-allowed disabled:opacity-30;
}
.display-remove {
  @apply hover:bg-red-50 hover:text-red-700;
}
.display-input {
  @apply h-7 w-full rounded border border-gray-300 bg-white px-2 text-[11px] text-gray-800 focus:outline-none focus:ring-1 focus:ring-neutral-500;
}
.display-label {
  @apply flex min-w-0 flex-col gap-1 text-gray-600;
}
</style>
